import { I18n } from "@assets/strings";
import React from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { Organization } from "@domain/organization/organization";
import { useIsMobile } from "@utils/hooks";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { row, stack } from "@ui/components/layout";
import { Menu } from "@ui/components/menus/menu";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { gap, mobile } from "@ui/styles/utils";

interface OrganizationSwitchProps extends React.HTMLAttributes<HTMLDivElement> {
	organization: Organization;
}
export const OrganizationSwitch: React.FC<OrganizationSwitchProps> = ({ organization, ...htmlProps }) => {
	const organizationAlias = useOrganizationAlias();
	const currentProfile = useCurrentProfile();
	const isMobile = useIsMobile();
	const { currentProfileService } = useServices();

	return (
		<Container {...htmlProps}>
			<ProfilePicture
				shadowed
				picture={organizationAlias ? organizationAlias.avatar : currentProfile?.avatar}
				size={40}
				fallbackType={organizationAlias ? organization.type : "USER"}
			/>
			<Primary>{I18n.t("organization.switch.message")}</Primary>
			<StyledMenu
				renderMenuContent={() => (
					<Content>
						<MenuSwitch selected={!organizationAlias} onClick={() => currentProfileService.setAlias(null)}>
							{I18n.t("organization.switch.user")}
						</MenuSwitch>
						<MenuSwitch selected={!!organizationAlias} onClick={() => currentProfileService.setAlias(organization)}>
							{I18n.t("organization.switch.admin")}
						</MenuSwitch>
					</Content>
				)}
				closeOnSelect
				bottomSheetMode={isMobile}
			>
				{organizationAlias ? I18n.t("organization.switch.admin") : I18n.t("organization.switch.user")}
			</StyledMenu>
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	padding: 20px;
	${row(5, "flex-start", "center")};
	@media ${mobile} {
		padding: 10px;
		${stack("S", "flex-start", "center")};
	}
`;

const StyledMenu = styled(Menu)`
	${({ theme }) => theme.textStyles.primary};
	color: ${({ theme }) => theme.colors.secondary};
`;

const MenuSwitch = styled(Primary)<{ selected?: boolean }>`
	cursor: pointer;
	${({ selected, theme }) => selected && `color: ${theme.colors.secondary}`}
`;

const Content = styled.div`
	${gap(10)};
	font-size: 0.8125rem;
`;
